import React, { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUser, useGraduateUser, useUpdateUser } from '../../hooks/api/user';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import GraduationDialog from './GraduationDialog';
import GraduationHistoryTable from './GraduationHistoryTable';
import GraduationProgressCard from './GraduationProgressCard';
import StudentProfileCard from './StudentProfileCard';
import { formatFullDate, getUserGraduationProgress } from './studentGraduation';

export default function StudentPage() {
  const { id } = useParams();
  const studentId = Number(id);
  const navigate = useNavigate();
  const { data: user } = useGetUser(Number.isNaN(studentId) ? undefined : studentId);
  const { currentUserId, isInstructor } = useCurrentUser();
  const graduateUserMutation = useGraduateUser();
  const updateUserMutation = useUpdateUser();
  const [graduationDialogOpen, setGraduationDialogOpen] = useState(false);
  const [selectedBelt, setSelectedBelt] = useState('');
  const [selectedStripes, setSelectedStripes] = useState('0');
  const [graduationNote, setGraduationNote] = useState('');

  const graduationHistory = useMemo(() => user?.graduationHistory ?? [], [user]);
  const totalBookings = user?.totalBookings ?? 0;
  const progress = useMemo(() => getUserGraduationProgress(user ?? {}), [user]);

  if (!user) return null;

  const paymentActive = Boolean(user.plan);
  const paymentLabel = paymentActive ? 'Cuota activa' : 'Sin cuota activa';
  const paymentHelper = paymentActive
    ? `${user.plan?.name ?? 'Plan activo'}${user.paidAt ? ` · desde ${formatFullDate(user.paidAt)}` : ''}`
    : 'Sin plan asignado';

  const openGraduationDialog = () => {
    setSelectedBelt(progress.nextBelt);
    setSelectedStripes(progress.nextStripes.toString());
    setGraduationNote('');
    setGraduationDialogOpen(true);
  };

  const handleGraduationSave = () => {
    graduateUserMutation.mutate(
      {
        id: user.id,
        graduation: {
          belt: selectedBelt,
          stripes: Number(selectedStripes),
          note: graduationNote,
        },
      },
      {
        onSuccess: () => setGraduationDialogOpen(false),
      },
    );
  };

  const handleAliasSave = (alias: string) => {
    updateUserMutation.mutate({
      id: user.id,
      changeset: { nickName: alias },
    });
  };

  const isCurrentUser = currentUserId === user.id;
  const canEditInstructor = isInstructor && !isCurrentUser;
  const isInstructorSaving =
    updateUserMutation.isLoading && updateUserMutation.variables?.changeset.instructor !== undefined;
  const handleInstructorToggle = (next: boolean) => {
    updateUserMutation.mutate({
      id: user.id,
      changeset: { instructor: next },
    });
  };

  return (
    <Box sx={{ display: 'grid', gap: { xs: 1.5, md: 2 } }}>
      <Button
        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
        onClick={() => navigate('/students')}
        size="small"
        sx={{ justifySelf: 'start', textTransform: 'none', color: '#666', px: 0, minWidth: 0 }}
      >
        Volver
      </Button>

      <StudentProfileCard
        user={user}
        totalBookings={totalBookings}
        paymentLabel={paymentLabel}
        paymentHelper={paymentHelper}
        paymentActive={paymentActive}
        isAliasSaving={updateUserMutation.isLoading}
        onAliasSave={handleAliasSave}
        isInstructorSaving={isInstructorSaving}
        canEditInstructor={canEditInstructor}
        onInstructorToggle={handleInstructorToggle}
      />

      <GraduationProgressCard progress={progress} onGraduate={openGraduationDialog} />

      <GraduationHistoryTable graduationHistory={graduationHistory} />

      <GraduationDialog
        open={graduationDialogOpen}
        progress={progress}
        selectedBelt={selectedBelt}
        selectedStripes={selectedStripes}
        graduationNote={graduationNote}
        isSaving={graduateUserMutation.isLoading}
        onClose={() => setGraduationDialogOpen(false)}
        onSave={handleGraduationSave}
        onBeltChange={setSelectedBelt}
        onStripesChange={setSelectedStripes}
        onNoteChange={setGraduationNote}
      />
    </Box>
  );
}
